import {TargetBlank} from '@wandb/weave/common/util/links';
import {Pill} from '@wandb/weave/components/Tag';
import React, {useMemo} from 'react';

import {Icon} from '../../../../Icon/Icon';
import {Tooltip} from '../../../../Tooltip';
import {Messages} from '../pages/ChatView/types';

const extractVariablesFromMessages = (messages: Messages): string[] => {
  const variables = new Set<string>();

  messages.forEach(message => {
    const content = typeof message.content === 'string' ? message.content : '';
    const matches = content.match(/\{([^}]+)\}/g);
    if (matches) {
      matches.forEach(match => {
        const varName = match.slice(1, -1).trim();
        if (varName) {
          variables.add(varName);
        }
      });
    }
  });

  return Array.from(variables).sort();
};

export interface MessageVariablesProps {
  messages: Messages;
}

export const MessageVariables: React.FC<MessageVariablesProps> = ({
  messages,
}) => {
  const variables = useMemo(() => {
    return extractVariablesFromMessages(messages);
  }, [messages]);
  return (
    <div className="mb-16">
      <div className="flex items-center gap-1 mb-2">
        <label className="text-sm font-semibold text-moon-500">
          Parameters
        </label>
        <Tooltip
          content={
            <div className="max-w-xs">
              <p className="mb-2">
                Parameters allow you to create reusable prompts with
                placeholders.
              </p>
              <p className="mb-2">
                Use{' '}
                <code className="bg-gray-100 px-1 rounded text-xs">
                  {'{variable_name}'}
                </code>{' '}
                syntax in your prompt text.
              </p>
              <p>
                When using the prompt, substitute values with{' '}
                <code className="bg-gray-100 px-1 rounded text-xs">
                  .format(variable_name="value")
                </code>
              </p>
            </div>
          }
          trigger={
            <Icon
              name="info"
              width={14}
              height={14}
              className="text-moon-500 cursor-help"
            />
          }
        />
      </div>

      {variables.length > 0 ? (
        <div className="">
          <div className="flex flex-wrap gap-2">
            {variables.map((variable: string) => (
              <Pill key={variable} label={'{' + variable + '}'} color="moon" />
            ))}
          </div>
        </div>
      ) : (
        <div className="">
          <p className="text-moon-600 font-['Source_Sans_Pro'] text-sm">
            No prompt parameters detected. Parameters use{' '}
            <code className="bg-moon-100 px-2 py-1 rounded text-xs font-mono">
              {'{variable}'}
            </code>{' '}
            syntax and can be substituted using{' '}
            <code className="bg-moon-100 px-2 py-1 rounded text-xs font-mono">
              .format()
            </code>
            . <br />
            <TargetBlank href="https://wandb.github.io/weave/guides/core-types/prompts">
              Read the docs
            </TargetBlank>{' '}
            for more information on parameterizing prompts.
          </p>
        </div>
      )}
    </div>
  );
};
